import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, wireRows, nextStd, STD_FUSE, STD_CAPACITOR_SIZES, nextStdCap,
  calcKVARMultiplier, NEC_460_RULES, PF_PENALTY_THRESHOLDS,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const PowerFactorCorrection = ({ onSave }: { onSave?: SaveFn }) => {
  const [loadKW, setLoadKW] = useState("100");
  const [currentPF, setCurrentPF] = useState("0.75");
  const [targetPF, setTargetPF] = useState("0.95");
  const [systemV, setSystemV] = useState(defaults.voltage);
  const [systemPhase, setSystemPhase] = useState<string>(defaults.phases);

  const kw = parseFloat(loadKW) || 0;
  const pfFrom = parseFloat(currentPF) || 0;
  const pfTo = parseFloat(targetPF) || 0;
  const volts = parseFloat(systemV) || 480;
  const isValid = kw > 0 && pfFrom > 0 && pfFrom < 1 && pfTo > pfFrom && pfTo <= 1;

  const multiplier = calcKVARMultiplier(pfFrom, pfTo);
  const requiredKVAR = kw * multiplier;
  const stdKVAR = nextStdCap(requiredKVAR);

  const kvaOld = kw / pfFrom;
  const kvarOld = kw * Math.tan(Math.acos(pfFrom));
  const ampsOld = systemPhase === "3" ? kvaOld * 1000 / (volts * Math.sqrt(3)) : kvaOld * 1000 / volts;
  const kvarNew = kvarOld - requiredKVAR;
  const kvaNew = Math.sqrt(kw * kw + kvarNew * kvarNew);
  const pfActual = kw / kvaNew;
  const ampsNew = systemPhase === "3" ? kvaNew * 1000 / (volts * Math.sqrt(3)) : kvaNew * 1000 / volts;
  const ampReduction = ampsOld - ampsNew;
  const kvaReduction = kvaOld - kvaNew;

  const capCurrent = systemPhase === "3" ? (stdKVAR * 1000) / (volts * Math.sqrt(3)) : (stdKVAR * 1000) / volts;
  const capConductorAmps = capCurrent * 1.35;
  const capOCPD = nextStd(capCurrent * 2.5, STD_FUSE);
  const capWire = wireRows.find(r => (r[3] as number) >= capConductorAmps);

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">Power factor correction calculator — Size capacitor banks to improve PF per NEC Article 460.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Load (kW)</label><Input type="number" inputMode="decimal" value={loadKW} onChange={e => setLoadKW(e.target.value.slice(0, 7))} placeholder="100" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Current PF</label><Input type="number" inputMode="decimal" value={currentPF} onChange={e => setCurrentPF(e.target.value.slice(0, 5))} placeholder="0.75" step="0.01" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Target PF</label><Input type="number" inputMode="decimal" value={targetPF} onChange={e => setTargetPF(e.target.value.slice(0, 5))} placeholder="0.95" step="0.01" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">System Voltage</label><Select value={systemV} onValueChange={setSystemV}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="208">208V</SelectItem><SelectItem value="240">240V</SelectItem><SelectItem value="480">480V</SelectItem><SelectItem value="600">600V</SelectItem></SelectContent></Select></div>
      </div>

      <div className="flex gap-2">
        {["1", "3"].map(ph => (
          <button key={ph} onClick={() => setSystemPhase(ph)} className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${systemPhase === ph ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{ph}Φ</button>
        ))}
      </div>

      {isValid && (
        <div className="space-y-3 animate-fade-in">
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Capacitor Bank Required</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Calculated</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{fmt(requiredKVAR, 1)}</div><div className="text-[10px] text-muted-foreground">kVAR</div></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Std. Bank</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{stdKVAR}</div><div className="text-[10px] text-muted-foreground">kVAR</div></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Actual PF</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{fmt(pfActual, 3)}</div><div className="text-[10px] text-muted-foreground">after correction</div></div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Before vs After Correction</h3>
          <div className="-mx-4 overflow-x-auto">
            <div className="mx-4 rounded-lg border border-border">
              <table className="w-full text-left">
                <thead><tr><th className="data-table-header">Parameter</th><th className="data-table-header">Before</th><th className="data-table-header">After</th><th className="data-table-header">Savings</th></tr></thead>
                <tbody>
                  {[
                    { label: "Power Factor", before: fmt(pfFrom, 3), after: fmt(pfActual, 3), savings: `+${fmt((pfActual - pfFrom) * 100, 1)}%` },
                    { label: "Apparent Power (kVA)", before: fmt(kvaOld, 1), after: fmt(kvaNew, 1), savings: fmt(kvaReduction, 1) },
                    { label: "Reactive Power (kVAR)", before: fmt(kvarOld, 1), after: fmt(kvarNew, 1), savings: fmt(kvarOld - kvarNew, 1) },
                    { label: "Line Current (A)", before: fmt(ampsOld, 1), after: fmt(ampsNew, 1), savings: fmt(ampReduction, 1) },
                    { label: "Real Power (kW)", before: fmt(kw, 1), after: fmt(kw, 1), savings: "—" },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                      <td className="data-table-cell text-xs font-semibold">{row.label}</td>
                      <td className="data-table-cell font-mono-data">{row.before}</td>
                      <td className="data-table-cell font-mono-data text-primary font-bold">{row.after}</td>
                      <td className="data-table-cell font-mono-data text-pipe-green font-semibold">{row.savings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Current Reduction</div><div className="font-mono-data text-lg font-bold text-pipe-green mt-1">{fmt(ampReduction, 1)}A</div><div className="text-[10px] text-muted-foreground">{fmt((ampReduction / ampsOld) * 100, 1)}% lower</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Capacity Freed</div><div className="font-mono-data text-lg font-bold text-pipe-green mt-1">{fmt(kvaReduction, 1)} kVA</div><div className="text-[10px] text-muted-foreground">{fmt((kvaReduction / kvaOld) * 100, 1)}% freed</div></div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Capacitor Circuit (NEC 460)</h3>
          <div className="trade-card border-conduit/30 bg-conduit/5 space-y-2">
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">Cap. Current:</span><span className="font-mono-data font-bold text-right">{fmt(capCurrent, 1)}A</span>
              <span className="text-muted-foreground">Conductor (135%):</span><span className="font-mono-data font-bold text-right">{capWire ? `${capWire[0]} AWG` : "—"} ({fmt(capConductorAmps, 1)}A)</span>
              <span className="text-muted-foreground">Max OCPD (250%):</span><span className="font-mono-data font-bold text-right">{capOCPD}A</span>
              <span className="text-muted-foreground">Bank Size:</span><span className="font-mono-data font-bold text-right">{stdKVAR} kVAR @ {systemV}V</span>
            </div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Utility PF Thresholds</h3>
          <div className="space-y-1.5">
            {PF_PENALTY_THRESHOLDS.map(t => (
              <div key={t.pf} className={`flex items-center justify-between trade-card py-2 ${pfActual >= t.pf ? "border-pipe-green/30 bg-pipe-green/5" : pfFrom < t.pf ? "border-destructive/20 bg-destructive/5" : ""}`}>
                <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${pfActual >= t.pf ? "bg-pipe-green" : pfFrom >= t.pf ? "bg-safety" : "bg-destructive"}`} /><span className="font-mono-data font-bold text-sm">{t.pf.toFixed(2)}</span></div>
                <span className="text-xs text-muted-foreground">{t.note}</span>
              </div>
            ))}
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">NEC Article 460 Requirements</h3>
          <div className="space-y-1.5">
            {NEC_460_RULES.map((item, i) => (<div key={i} className="flex gap-2 text-xs"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">{item.rule}</span><span className="text-muted-foreground">{item.text}</span></div>))}
          </div>
        </div>
      )}

      {isValid && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "power-factor-correction", label: `PF ${fmt(pfFrom, 2)} → ${fmt(pfActual, 3)} — ${kw} kW @ ${volts}V`,
            inputs: { load: `${kw} kW`, currentPF: fmt(pfFrom, 3), targetPF: fmt(pfTo, 3), voltage: `${volts}V`, phases: `${systemPhase}Φ` },
            results: [
              { label: "Capacitor Bank", value: String(stdKVAR), unit: "kVAR" },
              { label: "Actual PF", value: fmt(pfActual, 3), unit: "" },
              { label: "Current Reduction", value: fmt(ampReduction, 1), unit: "A" },
              { label: "kVA Freed", value: fmt(kvaReduction, 1), unit: "kVA" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      {!isValid && pfFrom > 0 && pfTo > 0 && pfTo <= pfFrom && (
        <div className="trade-card border-destructive/30 bg-destructive/5"><span className="text-sm text-destructive font-semibold">Target PF must be greater than current PF</span></div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ Do not over-correct to leading PF. For motor circuits, capacitor kVAR must not exceed motor no-load magnetizing kVAR (NEC 460.28).</p>
    </div>
  );
};
